import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Search, RefreshCw, Edit, Eye, EyeOff, Download,
    FileDown, Image as ImageIcon, Trash2, Plus, Link as LinkIcon,
    ArrowLeft, Calendar, Users, Ticket, Settings, Archive, CheckSquare, Square
} from 'lucide-react';
import ParticipantCard from '../../Component/v2/participant/ParticipantCard';
import EmbedFormModal from '../../Component/v2/participant/EmbedFormModal';
import EditAllCardsModal from '../../Component/v2/participant/EditAllCardsModal';
import DesignCustomizationModal from '../../Component/v2/idcard/DesignCustomizationModal';
import ManageCategoriesModal from '../../Component/v2/event/ManageCategoriesModal';
import ManageVolunteersModal from '../../Component/v2/event/ManageVolunteersModal';
import BulkUploadModal from '../../Component/v2/participant/BulkUploadModal';
import BulkAddAmenitiesModal from '../../Component/v2/participant/BulkAddAmenitiesModal';
import AmenitiesCheckInModal from '../../Component/v2/participant/AmenitiesCheckInModal';
import QuickAddParticipantModal from '../../Component/v2/participant/QuickAddParticipantModal';

const EventDetailsV2 = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showCards, setShowCards] = useState(true);
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const [showEditAllModal, setShowEditAllModal] = useState(false);
    const [showDesignModal, setShowDesignModal] = useState(false);
    const [showCategoriesModal, setShowCategoriesModal] = useState(false);
    const [showVolunteersModal, setShowVolunteersModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [showBulkAmenitiesModal, setShowBulkAmenitiesModal] = useState(false);
    const [showAmenitiesCheckInModal, setShowAmenitiesCheckInModal] = useState(false);
    const [showQuickAddModal, setShowQuickAddModal] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDownloading, setBulkDownloading] = useState(null); // null | 'png' | 'pdf'
    const [bulkProgress, setBulkProgress] = useState(0);
    const imgDataCache = useRef({}); // URL → base64 data URL, shared across all cards in one bulk run

    // Design settings state
    const [elementStyles, setElementStyles] = useState({});
    const [globalVisibility, setGlobalVisibility] = useState({});

    useEffect(() => {
        fetchEventAndParticipants();
    }, [eventId, showArchived]);

    // Fetch design settings after event is loaded
    useEffect(() => {
        if (event) {
            fetchDesignSettings();
        }
    }, [event]);

    const handleOpenDashboard = () => {
        if (localStorage.getItem('token') && event) {
            localStorage.setItem('v2ScanAuth', JSON.stringify({
                username: event.eventUsername || event._id?.toString(),
                password: event.eventPassword || '123456',
                role: 'full',
                eventInfo: { id: event._id, name: event.eventName },
            }));
        }
        navigate('/v2/events/dashboard');
    };

    const fetchEventAndParticipants = async () => {
        setLoading(true);
        try {
            // Fetch event details
            const eventRes = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}`);
            const eventData = await eventRes.json();
            if (eventData.success) {
                setEvent(eventData.data);
            }

            // Fetch participants
            const participantRes = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants?archived=${showArchived}`);
            const participantData = await participantRes.json();
            if (participantData.success) {
                setParticipants(participantData.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDesignSettings = async () => {
        if (!event) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/design`);
            const data = await response.json();
            if (data.success && data.data) {
                // Merge saved settings with dynamic defaults
                const dynamicDefaults = getDefaultElementStyles(event);
                const mergedStyles = { ...dynamicDefaults, ...data.data.elementStyles };
                const mergedVisibility = { ...getDefaultVisibility(event), ...data.data.visibility };

                setElementStyles(mergedStyles);
                setGlobalVisibility(mergedVisibility);
            } else {
                // No saved settings, use dynamic defaults
                setElementStyles(getDefaultElementStyles(event));
                setGlobalVisibility(getDefaultVisibility(event));
            }
        } catch (error) {
            console.error('Error fetching design settings:', error);
            // Fallback to dynamic defaults
            setElementStyles(getDefaultElementStyles(event));
            setGlobalVisibility(getDefaultVisibility(event));
        }
    };

    const handleCategoriesSaved = (updatedCategories) => {
        setEvent(prev => ({ ...prev, participantCategories: updatedCategories }));
    };

    const handleSaveDesignSettings = async (newElementStyles, newVisibility) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/design`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    elementStyles: newElementStyles,
                    visibility: newVisibility
                })
            });
            const data = await response.json();
            if (data.success) {
                setElementStyles(newElementStyles);
                setGlobalVisibility(newVisibility);
                alert('Design settings saved successfully!');
            }
        } catch (error) {
            console.error('Error saving design settings:', error);
            alert('Failed to save design settings');
        }
    };

    const handleDeleteParticipant = async (participantId) => {
        if (!window.confirm('Are you sure you want to delete this participant?')) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/${participantId}`,
                { method: 'DELETE' }
            );
            const data = await response.json();
            if (data.success) {
                setParticipants(participants.filter(p => p._id !== participantId));
            }
        } catch (error) {
            console.error('Error deleting participant:', error);
        }
    };

    const handleArchiveParticipant = async (participantId) => {
        if (!window.confirm('Are you sure you want to archive this participant?')) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/${participantId}/archive`,
                { method: 'PUT' }
            );
            const data = await response.json();
            if (data.success) {
                setParticipants(participants.filter(p => p._id !== participantId));
            }
        } catch (error) {
            console.error('Error archiving participant:', error);
        }
    };

    const handleRestoreParticipant = async (participantId) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/${participantId}/restore`,
                { method: 'PUT' }
            );
            const data = await response.json();
            if (data.success) {
                setParticipants(participants.filter(p => p._id !== participantId));
            }
        } catch (error) {
            console.error('Error restoring participant:', error);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const selectAll = () => setSelectedIds(new Set(filteredParticipants.map(p => p._id)));
    const deselectAll = () => setSelectedIds(new Set());

    const exitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Permanently delete ${selectedIds.size} participant(s)? This cannot be undone.`)) return;

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/bulk-delete`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids: [...selectedIds] })
                }
            );
            const data = await res.json();
            if (data.success) {
                setParticipants(prev => prev.filter(p => !selectedIds.has(p._id)));
                setSelectedIds(new Set());
                setSelectionMode(false);
            }
        } catch (error) {
            console.error('Bulk delete failed:', error);
        }
    };

    // ─── Canvas-based card generator ───────────────────────────────────────────
    // Draws the ID card directly on a Canvas2D context — no React off-screen
    // rendering, no html-to-image, no CORS issues.

    const CARD_W = 430;
    const CARD_H = 610;

    // Fetch URL through the server proxy and cache as base64 data URL.
    const fetchAsDataUrl = async (url) => {
        if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url;
        if (imgDataCache.current[url]) return imgDataCache.current[url];
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/proxy-image?url=${encodeURIComponent(url)}`
            );
            if (!res.ok) return null;
            const blob = await res.blob();
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            imgDataCache.current[url] = dataUrl;
            return dataUrl;
        } catch { return null; }
    };

    // Pre-fetch every image (backgrounds + profile photos) before rendering starts.
    const prefetchAllImages = async () => {
        const urls = new Set();
        if (event.defaultIdCardBackground) urls.add(event.defaultIdCardBackground);
        event.participantCategories?.forEach(c => { if (c.idCardBackground) urls.add(c.idCardBackground); });
        event.tickets?.forEach(t => { if (t.idCardBackground) urls.add(t.idCardBackground); });
        const fileFieldIds = (event.participantFields || []).filter(f => f.fieldType === 'file').map(f => f.id);
        participants.forEach(p => {
            fileFieldIds.forEach(id => { const v = p.data?.[id]; if (v) urls.add(v); });
        });
        await Promise.all([...urls].map(fetchAsDataUrl));
    };

    // Load a data URL into an HTMLImageElement (always resolves — never rejects).
    const loadImg = (src) => new Promise(resolve => {
        if (!src) return resolve(null);
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });

    // Resolve element position from a style spec (top/bottom + left).
    const resolvePos = (style, elemH) => {
        const x = style.left === '50%' ? CARD_W / 2
                : style.left === '100%' ? CARD_W
                : typeof style.left === 'number' ? style.left
                : CARD_W / 2;
        const y = style.top !== undefined ? style.top
                : CARD_H - (style.bottom || 0) - elemH;
        return { x, y };
    };

    // Get the background image URL for a participant.
    const getParticipantBg = (participant) => {
        if (participant.categoryId && event.participantCategories) {
            const cat = event.participantCategories.find(c => c.id === participant.categoryId);
            if (cat?.idCardBackground) return cat.idCardBackground;
        }
        if (participant.ticketId && event.tickets) {
            const ticket = event.tickets.find(t => t.id === participant.ticketId);
            if (ticket?.idCardBackground) return ticket.idCardBackground;
        }
        return event.defaultIdCardBackground || null;
    };

    // Draw a rounded rectangle path.
    const roundRect = (ctx, x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    };

    // Main canvas card renderer — returns a PNG data URL.
    const generateCardCanvas = async (participant) => {
        const SCALE = 2;
        const canvas = document.createElement('canvas');
        canvas.width = CARD_W * SCALE;
        canvas.height = CARD_H * SCALE;
        const ctx = canvas.getContext('2d');
        ctx.scale(SCALE, SCALE);

        // 1. Background
        const bgUrl = getParticipantBg(participant);
        const bgDataUrl = bgUrl ? imgDataCache.current[bgUrl] : null;
        if (bgDataUrl) {
            const bgImg = await loadImg(bgDataUrl);
            if (bgImg) ctx.drawImage(bgImg, 0, 0, CARD_W, CARD_H);
        } else {
            const grad = ctx.createLinearGradient(0, 0, CARD_W, CARD_H);
            grad.addColorStop(0, '#667eea');
            grad.addColorStop(1, '#764ba2');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, CARD_W, CARD_H);
        }

        // 2. Participant fields (text + profile photo)
        for (const field of (event.participantFields || [])) {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
            const style = elementStyles?.[fieldKey];
            const visible = globalVisibility?.[fieldKey];
            const value = participant.data?.[field.fieldName] ?? participant.data?.[field.id];
            if (!visible || !value || !style) continue;

            if (field.fieldType === 'file' && field.fieldName.toLowerCase().includes('picture')) {
                const size = style.size || 170;
                const { x, y } = resolvePos(style, size);
                const px = x - size / 2;
                const photoDataUrl = imgDataCache.current[value];
                if (photoDataUrl) {
                    const photo = await loadImg(photoDataUrl);
                    if (photo) {
                        // White border
                        ctx.save();
                        ctx.beginPath();
                        ctx.arc(px + size / 2, y + size / 2, size / 2 + 4, 0, Math.PI * 2);
                        ctx.fillStyle = 'white';
                        ctx.fill();
                        // Circular clip + draw
                        ctx.beginPath();
                        ctx.arc(px + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
                        ctx.clip();
                        ctx.drawImage(photo, px, y, size, size);
                        ctx.restore();
                    }
                }
            } else {
                const fontSize = style.fontSize || 16;
                const isName = field.fieldName.toLowerCase().includes('name');
                const isOrg = field.fieldName.toLowerCase().includes('institute') ||
                              field.fieldName.toLowerCase().includes('organization');
                const { x, y } = resolvePos(style, fontSize);
                ctx.font = `${isName ? 'bold' : 'normal'} ${fontSize}px Arial, sans-serif`;
                ctx.fillStyle = style.color || 'black';
                ctx.textAlign = style.textAlign || 'center';
                ctx.textBaseline = 'top';
                const text = isOrg ? String(value).toUpperCase() : String(value);
                ctx.fillText(text, x, y, CARD_W * 0.9);
            }
        }

        // 3. QR code
        if (globalVisibility?.qrCode && elementStyles?.qrCode) {
            const qrSt = elementStyles.qrCode;
            const qrSize = qrSt.size || 100;
            const qrX = (qrSt.left === '50%' ? CARD_W / 2 : (typeof qrSt.left === 'number' ? qrSt.left : CARD_W / 2));
            const PAD = 8, LABEL_H = 22;
            const boxW = qrSize + PAD * 2;
            const boxH = LABEL_H + qrSize + PAD;
            // boxY: top of the white box — match CSS bottom positioning exactly
            const boxY = qrSt.top !== undefined
                ? qrSt.top - LABEL_H
                : CARD_H - (qrSt.bottom || 0) - boxH;
            const qrY = boxY + LABEL_H; // QR image starts right after label
            const boxX = qrX - boxW / 2;
            try {
                const QRLib = (await import('qrcode')).default;
                const qrDataUrl = await QRLib.toDataURL(
                    `${window.location.origin}/v2/scan/${participant._id}`,
                    { width: qrSize * SCALE, margin: 1, errorCorrectionLevel: 'H' }
                );
                const qrImg = await loadImg(qrDataUrl);
                if (qrImg) {
                    ctx.save();
                    ctx.shadowColor = 'rgba(0,0,0,0.15)';
                    ctx.shadowBlur = 6;
                    ctx.fillStyle = 'white';
                    roundRect(ctx, boxX, boxY, boxW, boxH, 8);
                    ctx.fill();
                    ctx.restore();
                    ctx.font = 'bold 11px Arial, sans-serif';
                    ctx.fillStyle = 'black';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText('Show at the Venue', qrX, boxY + LABEL_H / 2);
                    ctx.drawImage(qrImg, qrX - qrSize / 2, qrY, qrSize, qrSize);
                }
            } catch (e) { /* skip QR if generation fails */ }
        }

        // 4. Participant Category (free events)
        if (event.eventType === 'free' && globalVisibility?.categoryName && elementStyles?.categoryName && participant.categoryId) {
            const cat = event.participantCategories?.find(c => c.id === participant.categoryId);
            if (cat?.name) {
                const st = elementStyles.categoryName;
                const fontSize = st.fontSize || 16;
                const x = st.left === '50%' ? CARD_W / 2 : (typeof st.left === 'number' ? st.left : CARD_W / 2);
                const y = st.top !== undefined ? st.top : CARD_H - (st.bottom || 0) - fontSize - 2;
                const PAD_X = 6, PAD_Y = 6, R = 8;
                ctx.font = `bold ${fontSize}px Arial, sans-serif`;
                ctx.textAlign = st.textAlign || 'center';
                ctx.textBaseline = 'top';
                const textW = ctx.measureText(cat.name).width;
                const boxW = textW + PAD_X * 2;
                const boxH = fontSize + PAD_Y * 2;
                const boxX = x - boxW / 2;
                // Use saved bgColor + bgOpacity
                const hex = st.bgColor || '#ffffff';
                const opacity = st.bgOpacity ?? 0.9;
                const r = parseInt(hex.slice(1,3),16);
                const g = parseInt(hex.slice(3,5),16);
                const b = parseInt(hex.slice(5,7),16);
                ctx.fillStyle = `rgba(${r},${g},${b},${opacity})`;
                ctx.beginPath();
                ctx.roundRect(boxX, y - PAD_Y, boxW, boxH, R);
                ctx.fill();
                ctx.fillStyle = st.color || 'black';
                ctx.fillText(cat.name, x, y);
            }
        }

        // 5. Registration ID
        if (globalVisibility?.participantId && elementStyles?.participantId && participant.registrationId) {
            const idSt = elementStyles.participantId;
            const fontSize = idSt.fontSize || 12;
            const idX = idSt.left === '50%' ? CARD_W / 2 : (typeof idSt.left === 'number' ? idSt.left : CARD_W / 2);
            const idY = idSt.top !== undefined ? idSt.top : CARD_H - (idSt.bottom || 0) - fontSize - 2;
            ctx.font = `${fontSize}px Arial, sans-serif`;
            ctx.fillStyle = idSt.color || 'black';
            ctx.textAlign = idSt.textAlign || 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(`ID: ${participant.registrationId}`, idX, idY);
        }

        return canvas.toDataURL('image/png');
    };
    // ───────────────────────────────────────────────────────────────────────────

    const handleDownloadAllPNG = async () => {
        const toDownload = filteredParticipants;
        if (toDownload.length === 0) return;
        setBulkDownloading('png');
        setBulkProgress(0);
        imgDataCache.current = {};
        try {
            await prefetchAllImages();
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            const total = toDownload.length;
            const nameField = event.participantFields?.[0];

            for (let i = 0; i < total; i++) {
                const participant = toDownload[i];
                const dataUrl = await generateCardCanvas(participant);
                const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
                const rawName = (nameField && (participant.data?.[nameField.fieldName] ?? participant.data?.[nameField.id])) ||
                    participant.registrationId || participant._id;
                zip.file(`${String(rawName).replace(/[^a-zA-Z0-9-_]/g, '_')}.png`, base64, { base64: true });
                setBulkProgress(Math.round((i + 1) / total * 100));
            }

            const blob = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${event.eventName}-cards.zip`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PNG:', error);
            alert(`Failed: ${error?.message || error}`);
        } finally {
            setBulkDownloading(null);
            setBulkProgress(0);
        }
    };

    const handleDownloadAllPDF = async () => {
        const toDownload = filteredParticipants;
        if (toDownload.length === 0) return;
        setBulkDownloading('pdf');
        setBulkProgress(0);
        imgDataCache.current = {};
        try {
            await prefetchAllImages();
            const { jsPDF } = await import('jspdf');
            // Use mm for accurate physical dimensions (96dpi: 1px = 0.2646mm)
            const MM = 25.4 / 96;
            const mmW = CARD_W * MM;
            const mmH = CARD_H * MM;
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [mmW, mmH] });
            const total = toDownload.length;

            for (let i = 0; i < total; i++) {
                if (i > 0) pdf.addPage([mmW, mmH]);
                const dataUrl = await generateCardCanvas(toDownload[i]);
                pdf.addImage(dataUrl, 'PNG', 0, 0, mmW, mmH);
                setBulkProgress(Math.round((i + 1) / total * 100));
            }

            pdf.save(`${event.eventName}-cards.pdf`);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert(`Failed: ${error?.message || error}`);
        } finally {
            setBulkDownloading(null);
            setBulkProgress(0);
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const filteredParticipants = participants.filter(participant => {
        // Category filter
        if (selectedCategory && participant.categoryId !== selectedCategory) return false;

        // Text search
        const q = searchTerm.toLowerCase().trim();
        if (!q) return true;

        // All participant data fields
        if (Object.values(participant.data || {}).some(v => String(v).toLowerCase().includes(q))) return true;
        // Registration ID
        if (participant.registrationId?.toLowerCase().includes(q)) return true;
        // Category name
        const cat = participant.categoryId && event?.participantCategories?.find(c => c.id === participant.categoryId);
        if (cat?.name.toLowerCase().includes(q)) return true;

        return false;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);
    const paginatedParticipants = filteredParticipants.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Reset to page 1 when search / filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategory, showArchived]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <button
                        onClick={() => navigate('/v2/events')}
                        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Events
                    </button>

                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{event?.eventName}</h1>
                            <div className="flex items-center space-x-4 mt-2">
                                <div className="flex items-center text-sm text-gray-600">
                                    <Calendar className="w-4 h-4 mr-1" />
                                    {new Date(event?.eventDate).toLocaleDateString()}
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <Users className="w-4 h-4 mr-1" />
                                    {participants.length} Participants
                                </div>
                                {event?.eventType === 'paid' && (
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Ticket className="w-4 h-4 mr-1" />
                                        {event?.tickets?.length || 0} Ticket Types
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setShowQuickAddModal(true)}
                                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Quick Add
                            </button>
                            <button
                                onClick={() => setShowEmbedModal(true)}
                                className="inline-flex items-center px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700"
                            >
                                <LinkIcon className="w-4 h-4 mr-2" />
                                Embed Forms
                            </button>
                            <button
                                onClick={() => navigate(`/v2/events/${eventId}/edit`)}
                                className="inline-flex items-center px-3 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700"
                            >
                                <Settings className="w-4 h-4 mr-2" />
                                Event Settings
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        {/* Search + Category Filter */}
                        <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-2xl">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name, email, phone, reg ID, category…"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            {event?.participantCategories?.length > 0 && (
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="py-2 pl-3 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shrink-0"
                                >
                                    <option value="">All Categories</option>
                                    {event.participantCategories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* View Filters */}
                        <div className="flex rounded-lg bg-gray-100 p-1">
                            <button
                                onClick={() => setShowArchived(false)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${!showArchived ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                            >
                                Active
                            </button>
                            <button
                                onClick={() => setShowArchived(true)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${showArchived ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                            >
                                Archived
                            </button>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center flex-wrap gap-2">
                            <button
                                onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
                                className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    selectionMode
                                        ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {selectionMode ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
                                {selectionMode ? `Selecting (${selectedIds.size})` : 'Select'}
                            </button>
                            <button
                                onClick={fetchEventAndParticipants}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reload
                            </button>

                            <button
                                onClick={() => setShowEditAllModal(true)}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit All Cards
                            </button>
                            <button
                                onClick={() => setShowDesignModal(true)}
                                className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 shadow-md"
                            >
                                <Settings className="w-4 h-4 mr-2" />
                                Customize Design
                            </button>
                            {event?.eventType === 'free' && (
                                <button
                                    onClick={() => setShowCategoriesModal(true)}
                                    className="inline-flex items-center px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
                                >
                                    <Users className="w-4 h-4 mr-2" />
                                    Manage Categories
                                    {event.participantCategories?.length > 0 && (
                                        <span className="ml-2 bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">
                                            {event.participantCategories.length}
                                        </span>
                                    )}
                                </button>
                            )}
                            <button
                                onClick={() => setShowVolunteersModal(true)}
                                className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 shadow-sm transition-colors"
                            >
                                <span className="mr-2">🛡️</span>
                                Volunteers
                                {event?.volunteers?.length > 0 && (
                                    <span className="ml-2 bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                                        {event.volunteers.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => navigate('/v2/events/scan')}
                                className="inline-flex items-center px-3 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900"
                            >
                                <span className="mr-2">📷</span>
                                Scan / Check-in
                            </button>
                            <button
                                onClick={handleOpenDashboard}
                                className="inline-flex items-center px-3 py-2 bg-indigo-700 text-white text-sm font-medium rounded-lg hover:bg-indigo-800"
                            >
                                <span className="mr-2">📊</span>
                                Dashboard
                            </button>
                            <button
                                onClick={() => setShowCards(!showCards)}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                            >
                                {showCards ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                {showCards ? 'Hide' : 'Show'} Cards
                            </button>

                            <button
                                onClick={() => setShowBulkUploadModal(true)}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
                            >
                                <FileDown className="w-4 h-4 mr-2" />
                                Bulk Upload
                            </button>

                            {(event?.amenities?.length > 0 || event?.participantCategories?.some(c => c.amenities?.length > 0)) && (
                                <>
                                    <button
                                        onClick={() => setShowBulkAmenitiesModal(true)}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
                                    >
                                        <span className="mr-2">🎁</span>
                                        Add Amenities
                                    </button>
                                    <button
                                        onClick={() => setShowAmenitiesCheckInModal(true)}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700"
                                    >
                                        <span className="mr-2">📋</span>
                                        Amenity Stats
                                    </button>
                                </>
                            )}

                            <button
                                onClick={handleDownloadAllPNG}
                                disabled={bulkDownloading !== null}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <ImageIcon className="w-4 h-4 mr-2" />
                                {bulkDownloading === 'png' ? `PNG ${bulkProgress}%` : `Bulk PNG${filteredParticipants.length !== participants.length ? ` (${filteredParticipants.length})` : ''}`}
                            </button>

                            <button
                                onClick={handleDownloadAllPDF}
                                disabled={bulkDownloading !== null}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <FileDown className="w-4 h-4 mr-2" />
                                {bulkDownloading === 'pdf' ? `PDF ${bulkProgress}%` : `Bulk PDF${filteredParticipants.length !== participants.length ? ` (${filteredParticipants.length})` : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Participants Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {filteredParticipants.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No participants found</h3>
                        <p className="text-gray-600 mb-4">
                            {searchTerm ? `No participants matching "${searchTerm}"` : "Share your registration form to get participants"}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => setShowEmbedModal(true)}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                            >
                                <LinkIcon className="w-4 h-4 mr-2" />
                                Get Registration Links
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {paginatedParticipants.map((participant, index) => (
                                <ParticipantCard
                                    key={participant._id}
                                    participant={participant}
                                    event={event}
                                    showCard={showCards}
                                    onDelete={() => handleDeleteParticipant(participant._id)}
                                    onRefresh={fetchEventAndParticipants}
                                    elementStyles={elementStyles}
                                    globalVisibility={globalVisibility}
                                    index={index}
                                    onArchive={handleArchiveParticipant}
                                    onRestore={handleRestoreParticipant}
                                    selectionMode={selectionMode}
                                    selected={selectedIds.has(participant._id)}
                                    onSelect={() => toggleSelect(participant._id)}
                                />
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-8 rounded-lg shadow-sm">
                                {/* Mobile: prev/next only */}
                                <div className="flex flex-1 justify-between sm:hidden">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm text-gray-600 self-center">{currentPage} / {totalPages}</span>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>

                                {/* Desktop: windowed pagination */}
                                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                    <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>–<span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredParticipants.length)}</span> of <span className="font-medium">{filteredParticipants.length}</span>
                                    </p>
                                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                                        {/* Prev */}
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" /></svg>
                                        </button>

                                        {/* Windowed page numbers */}
                                        {(() => {
                                            const pages = [];
                                            const delta = 2; // pages on each side of current
                                            const left = Math.max(2, currentPage - delta);
                                            const right = Math.min(totalPages - 1, currentPage + delta);

                                            const btnClass = (active) => `relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                                active
                                                    ? 'z-10 bg-blue-600 text-white'
                                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                            }`;

                                            // Always show page 1
                                            pages.push(
                                                <button key={1} onClick={() => handlePageChange(1)} className={btnClass(currentPage === 1)}>1</button>
                                            );

                                            // Left ellipsis
                                            if (left > 2) {
                                                pages.push(<span key="l-ellipsis" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">…</span>);
                                            }

                                            // Window around current page
                                            for (let p = left; p <= right; p++) {
                                                pages.push(
                                                    <button key={p} onClick={() => handlePageChange(p)} className={btnClass(currentPage === p)}>{p}</button>
                                                );
                                            }

                                            // Right ellipsis
                                            if (right < totalPages - 1) {
                                                pages.push(<span key="r-ellipsis" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">…</span>);
                                            }

                                            // Always show last page
                                            if (totalPages > 1) {
                                                pages.push(
                                                    <button key={totalPages} onClick={() => handlePageChange(totalPages)} className={btnClass(currentPage === totalPages)}>{totalPages}</button>
                                                );
                                            }

                                            return pages;
                                        })()}

                                        {/* Next */}
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Bulk selection action bar */}
            {selectionMode && (
                <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-semibold text-gray-800">
                                {selectedIds.size} selected
                            </span>
                            <button
                                onClick={selectAll}
                                className="text-sm text-blue-600 hover:underline font-medium"
                            >
                                Select all {filteredParticipants.length}
                            </button>
                            {selectedIds.size > 0 && (
                                <button
                                    onClick={deselectAll}
                                    className="text-sm text-gray-500 hover:underline"
                                >
                                    Deselect all
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={exitSelectionMode}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBulkDelete}
                                disabled={selectedIds.size === 0}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Trash2 className="w-4 h-4 mr-1.5" />
                                Delete {selectedIds.size > 0 ? selectedIds.size : ''} Selected
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showQuickAddModal && event && (
                <QuickAddParticipantModal
                    event={event}
                    onClose={() => setShowQuickAddModal(false)}
                    onSuccess={fetchEventAndParticipants}
                />
            )}

            {showEmbedModal && (
                <EmbedFormModal
                    event={event}
                    onClose={() => setShowEmbedModal(false)}
                />
            )}

            {showEditAllModal && (
                <EditAllCardsModal
                    event={event}
                    onClose={() => setShowEditAllModal(false)}
                    onSave={fetchEventAndParticipants}
                />
            )}

            {showDesignModal && (
                <DesignCustomizationModal
                    event={event}
                    onClose={() => setShowDesignModal(false)}
                    onSave={handleSaveDesignSettings}
                    initialStyles={elementStyles}
                    initialVisibility={globalVisibility}
                />
            )}

            {showCategoriesModal && event && (
                <ManageCategoriesModal
                    event={event}
                    onClose={() => setShowCategoriesModal(false)}
                    onSaved={handleCategoriesSaved}
                />
            )}

            {showVolunteersModal && event && (
                <ManageVolunteersModal
                    event={event}
                    onClose={() => setShowVolunteersModal(false)}
                    onSaved={(updatedVolunteers) => setEvent(prev => ({ ...prev, volunteers: updatedVolunteers }))}
                />
            )}

            {showBulkUploadModal && event && (
                <BulkUploadModal
                    event={event}
                    onClose={() => setShowBulkUploadModal(false)}
                    onImported={() => {
                        setShowBulkUploadModal(false);
                        fetchEventAndParticipants();
                    }}
                />
            )}

            {showBulkAmenitiesModal && event && (
                <BulkAddAmenitiesModal
                    event={event}
                    onClose={() => setShowBulkAmenitiesModal(false)}
                    onDone={fetchEventAndParticipants}
                />
            )}

            {showAmenitiesCheckInModal && event && (
                <AmenitiesCheckInModal
                    event={event}
                    participants={participants}
                    onClose={() => setShowAmenitiesCheckInModal(false)}
                />
            )}
        </div>
    );
};

// Helper function to get default element styles dynamically based on event fields
function getDefaultElementStyles(event) {
    const styles = {};
    let currentTop = 163; // Starting position for text fields

    // Generate styles for each participant field
    if (event?.participantFields) {
        event.participantFields.forEach((field, index) => {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');

            // Profile picture gets special treatment
            if (field.fieldType === 'file' && field.fieldName.toLowerCase().includes('picture')) {
                styles[fieldKey] = {
                    bottom: 160,
                    left: "50%",
                    size: 170
                };
            } else {
                // Text fields - stack them vertically
                const fontSize = field.fieldName.toLowerCase().includes('name') ? 29 :
                    field.fieldName.toLowerCase().includes('email') ? 14 : 16;

                styles[fieldKey] = {
                    top: currentTop,
                    left: "50%",
                    fontSize,
                    color: "black",
                    textAlign: "center"
                };

                currentTop += fontSize + 10; // Add spacing between fields
            }
        });
    }

    // Always add QR code and registration ID
    styles.qrCode = {
        bottom: 215,
        left: "50%",
        size: 100
    };

    styles.participantId = {
        bottom: 3,
        left: "50%",
        fontSize: 12,
        color: "black",
        textAlign: "center"
    };

    // Add category name field for free events with categories
    if (event?.eventType === 'free' && event?.participantCategories?.length > 0) {
        styles.categoryName = {
            bottom: 174,
            left: "50%",
            fontSize: 16,
            color: "black",
            textAlign: "center"
        };
    }

    // Add ticket fields for paid events
    if (event?.eventType === 'paid') {
        styles.ticketCategory = {
            bottom: 174,
            left: "50%",
            fontSize: 16,
            color: "black",
            textAlign: "center"
        };

        styles.ticketDates = {
            bottom: 102,
            left: "50%",
            fontSize: 17,
            color: "black",
            textAlign: "center"
        };
    }

    return styles;
}

// Helper function to get default visibility dynamically based on event fields
function getDefaultVisibility(event) {
    const visibility = {};

    // All participant fields visible by default
    if (event?.participantFields) {
        event.participantFields.forEach(field => {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
            visibility[fieldKey] = true;
        });
    }

    // System fields
    visibility.qrCode = true;
    visibility.participantId = true;
    visibility.isLandscapePrint = false;

    // Category name (hidden by default for free events — admin opts in)
    if (event?.eventType === 'free' && event?.participantCategories?.length > 0) {
        visibility.categoryName = false;
    }

    // Ticket fields (hidden by default for paid events)
    if (event?.eventType === 'paid') {
        visibility.ticketCategory = false;
        visibility.ticketDates = false;
    }

    return visibility;
}

export default EventDetailsV2;